import { useEffect, useMemo, useState } from "react";
import { type ActivityDay } from "@shared/api";
import { useActivityCalendarData } from "./useActivityCalendarData";

const CURRENT_YEAR = new Date().getFullYear();

interface CalendarCell {
  key: string;
  day: ActivityDay | null;
  level: number;
}

interface ActivityTooltipState {
  day: ActivityDay;
  x: number;
  y: number;
  placement: "above" | "below";
  align: "left" | "center" | "right";
}

export function ActivityCalendar({ refreshKey }: { refreshKey: number }) {
  const [tooltip, setTooltip] = useState<ActivityTooltipState | null>(null);
  const [year, setYear] = useState(CURRENT_YEAR);
  const { data, error, hasPreviousYear, hasNextYear } = useActivityCalendarData(year, refreshKey);

  useEffect(() => {
    setTooltip(null);
  }, [data]);

  const today = useMemo(() => localDayKey(new Date()), []);
  const summary = useMemo(() => (data ? summarizeActivity(data.days, today) : null), [data, today]);
  const cells = useMemo(() => (data ? buildCalendar(data.days) : []), [data]);

  return (
    <section className="activity-section">
      <div className="section-heading">
        <h2>Activity calendar</h2>
        <div className="activity-year-switcher" role="group" aria-label="Activity calendar year">
          <button
            className="secondary-button"
            type="button"
            onClick={() => setYear((value) => value - 1)}
            disabled={!hasPreviousYear}
            aria-label="Show previous year activity"
          >
            ←
          </button>
          <span className="activity-year-label">{year}</span>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setYear((value) => value + 1)}
            disabled={!hasNextYear}
            aria-label="Show next year activity"
          >
            →
          </button>
        </div>
      </div>

      {error ? (
        <div className="empty">Could not load activity: {error}</div>
      ) : !data || !summary ? (
        <div className="empty">Loading activity...</div>
      ) : (
        <>
          <div className="activity-summary">
            <ActivityStat label="Active time" value={formatMinutes(summary.totalMinutes)} />
            <ActivityStat label="Active days" value={summary.activeDays.toLocaleString()} />
            <ActivityStat label="Longest streak" value={`${summary.longestStreak.toLocaleString()}d`} />
            <ActivityStat label="Current streak" value={`${summary.currentStreak.toLocaleString()}d`} />
          </div>
          <div className="activity-calendar-wrap" onMouseLeave={() => setTooltip(null)} onScroll={() => setTooltip(null)}>
            <div className="activity-calendar" aria-label={`Activity from ${data.from} to ${data.to}`}>
              {cells.map((cell) => {
                const day = cell.day;
                if (!day) return <span key={cell.key} className="activity-cell is-empty" aria-hidden="true" />;
                return (
                  <span
                    key={cell.key}
                    className={`activity-cell level-${cell.level}${day.date === today ? " is-today" : ""}`}
                    tabIndex={0}
                    aria-label={`${day.date}: ${day.matches} matches, ${formatMinutes(day.minutes)}`}
                    onBlur={() => setTooltip((current) => (current?.day.date === day.date ? null : current))}
                    onFocus={(event) => setTooltip(positionTooltip(day, event.currentTarget))}
                    onMouseEnter={(event) => setTooltip(positionTooltip(day, event.currentTarget))}
                    onMouseMove={(event) => setTooltip(positionTooltip(day, event.currentTarget))}
                  />
                );
              })}
            </div>
            {tooltip && (
              <div
                className={`chart-tooltip activity-tooltip is-${tooltip.placement} is-${tooltip.align}`}
                role="tooltip"
                style={{ left: tooltip.x, top: tooltip.y }}
              >
                <strong>{formatTooltipDate(tooltip.day.date, today)}</strong>
                <div className="chart-tooltip-grid">
                  <span>Time played</span>
                  <b>{formatMinutes(tooltip.day.minutes)}</b>
                  <span>Matches</span>
                  <b>{tooltip.day.matches.toLocaleString()}</b>
                  <span>Kills / Deaths</span>
                  <b>{tooltip.day.kills.toLocaleString()} / {tooltip.day.deaths.toLocaleString()}</b>
                  <span>Wins / Losses</span>
                  <b>{tooltip.day.wins.toLocaleString()} / {tooltip.day.losses.toLocaleString()}</b>
                  <span>Score</span>
                  <b>{tooltip.day.score.toLocaleString()}</b>
                  <span>Damage</span>
                  <b>{tooltip.day.damage.toLocaleString()}</b>
                </div>
              </div>
            )}
          </div>
          <div className="activity-legend">
            <span>Less</span>
            <span className="activity-cell level-0" aria-hidden="true" />
            <span className="activity-cell level-1" aria-hidden="true" />
            <span className="activity-cell level-2" aria-hidden="true" />
            <span className="activity-cell level-3" aria-hidden="true" />
            <span className="activity-cell level-4" aria-hidden="true" />
            <span>More</span>
          </div>
        </>
      )}
    </section>
  );
}

function ActivityStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="activity-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function buildCalendar(days: ActivityDay[]): CalendarCell[] {
  if (days.length === 0) return [];
  const max = Math.max(...days.map((day) => day.minutes), 0);
  const first = days[0];
  if (!first) return [];
  const firstDay = calendarDate(first.date).getDay();
  const cells: CalendarCell[] = [];
  for (let i = 0; i < firstDay; i++) cells.push({ key: `pad-${i}`, day: null, level: 0 });
  for (const day of days) {
    const value = day.minutes;
    cells.push({ key: day.date, day, level: value <= 0 || max <= 0 ? 0 : Math.max(1, Math.ceil((value / max) * 4)) });
  }
  return cells;
}

function summarizeActivity(days: ActivityDay[], today: string) {
  const visibleDays = days.filter((day) => day.date <= today);
  let activeDays = 0;
  let totalMinutes = 0;
  let longestStreak = 0;
  let streak = 0;

  for (const day of visibleDays) {
    totalMinutes += day.minutes;
    if (day.minutes > 0 || day.matches > 0) {
      activeDays++;
      streak++;
      longestStreak = Math.max(longestStreak, streak);
    } else {
      streak = 0;
    }
  }

  let currentStreak = 0;
  for (let i = visibleDays.length - 1; i >= 0; i--) {
    const day = visibleDays[i];
    if (!day || (day.minutes <= 0 && day.matches <= 0)) break;
    currentStreak++;
  }

  return { activeDays, totalMinutes, longestStreak, currentStreak };
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function formatDate(date: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(calendarDate(date));
}

function formatTooltipDate(date: string, today: string): string {
  return date === today ? `${formatDate(date)} · Today` : formatDate(date);
}

function localDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function calendarDate(date: string): Date {
  return new Date(`${date}T12:00:00`);
}

function positionTooltip(day: ActivityDay, node: HTMLElement): ActivityTooltipState {
  const rect = node.getBoundingClientRect();
  const placement = rect.top < 140 ? "below" : "above";
  const edgePadding = 20;
  const tooltipWidth = 320;
  const centerX = rect.left + rect.width / 2;
  const nearLeftEdge = centerX < edgePadding + tooltipWidth / 2;
  const nearRightEdge = centerX > window.innerWidth - edgePadding - tooltipWidth / 2;

  let align: ActivityTooltipState["align"] = "center";
  let x = centerX;

  if (nearLeftEdge) {
    align = "left";
    x = Math.max(rect.left, edgePadding);
  } else if (nearRightEdge) {
    align = "right";
    x = Math.min(rect.right, window.innerWidth - edgePadding);
  }

  return {
    day,
    x,
    y: placement === "above" ? rect.top - 10 : rect.bottom + 10,
    placement,
    align,
  };
}
